import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './generated/schema.js';

export type { components, paths };
export type JervisApiClient = Client<paths>;

/** RFC 9457 problem details returned by the API for every error. */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance: string;
  correlationId: string;
  errors?: unknown;
}

export const CORRELATION_HEADER = 'x-correlation-id';

export const CSRF_HEADER = 'x-csrf-token';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface ApiClientOptions {
  /** Origin of the API; empty string = same origin (web dev proxy / reverse proxy). */
  baseUrl?: string;
  /** Custom transport; openapi-fetch always passes a single Request. */
  fetch?: (request: Request) => Promise<Response>;
  /** Supplies a correlation id per request (e.g. desktop command id). Omit to let the server mint one. */
  correlationId?: () => string | undefined;
  /** Supplies the CSRF token (read from the readable `jervis_csrf` cookie) sent on unsafe methods. */
  csrfToken?: () => string | undefined;
}

/** Generated, fully typed client used by web and desktop (ADR-010). */
export function createApiClient(options: ApiClientOptions = {}): JervisApiClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? '',
    credentials: 'include',
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  if (options.correlationId) {
    const supply = options.correlationId;
    const correlation: Middleware = {
      onRequest({ request }) {
        const id = supply();
        if (id !== undefined) {
          request.headers.set(CORRELATION_HEADER, id);
        }
        return request;
      },
    };
    client.use(correlation);
  }

  if (options.csrfToken) {
    const supply = options.csrfToken;
    const csrf: Middleware = {
      onRequest({ request }) {
        if (!SAFE_METHODS.has(request.method)) {
          const token = supply();
          if (token !== undefined) {
            request.headers.set(CSRF_HEADER, token);
          }
        }
        return request;
      },
    };
    client.use(csrf);
  }

  return client;
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ProblemDetails).type === 'string' &&
    typeof (value as ProblemDetails).status === 'number'
  );
}
